import clsx from 'clsx';
import { castArray, isObject, some, sortBy } from 'lodash-es';
import { MouseEvent, ReactNode, isValidElement } from 'react';
import { UseTranslationResponse } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { SIDEBAR_ITEM_ORDER_NAMESPACE } from 'consts';
import { SidebarMenuItemEntity } from 'netline-ui';
import services from 'services';
import { AppModuleSidebarItem, RouteType, SidebarItem } from 'types';
import { SidebarCustomizationMap, getMainConfig } from 'utils';

import AnchorLink from './AnchorLink';
import { SavedSidebarItem, SavedSidebarItemBase } from './SidebarNavigation.types';

const mapByRouteType = ({
  routeType,
  id,
  target,
  noopener,
  noreferrer,
}: SidebarItem): Partial<SidebarMenuItemEntity> => {
  if (routeType === RouteType.Url) {
    return {
      component: AnchorLink,
      rel: clsx({ noopener }, { noreferrer }),
      target: target || id,
    };
  }

  return {
    component: NavLink,
  };
};

type TranslationProps = {
  t: UseTranslationResponse<'translation', undefined>['t'];
  exists: UseTranslationResponse<'translation', undefined>['i18n']['exists'];
};

export const createMenuItem = (item: SidebarItem, { t, exists }: TranslationProps): SidebarMenuItemEntity => {
  const { key, id, className, Icon, text, route, priority } = item as AppModuleSidebarItem;

  return {
    key,
    id,
    className,
    Icon,
    priority,
    text: exists(text) ? t(text) : text,
    link: route,
    ...mapByRouteType(item),
  };
};

export const findParentWithClass = (target: Element | null, className: string) => {
  let currentNode = target;

  while (currentNode !== null) {
    if (currentNode.classList && currentNode.classList.contains(className)) {
      return currentNode;
    }
    currentNode = currentNode.parentNode as Element | null;
  }

  return null;
};

export const findSVGElement = (target: Element) => {
  if (target.tagName === 'svg') {
    return target;
  }
  if (target.tagName === 'path') {
    return target.parentNode;
  }

  const svgElement = target.querySelector('svg');
  return svgElement ?? null;
};

const DEFAULT_GROUP = 'DEFAULT_GROUP';

export const normalizeModules = (
  modules: SidebarItem[],
  { t, exists }: TranslationProps,
  handleRightClick: (event: MouseEvent<Element>, route: string) => void,
): SidebarMenuItemEntity[] => {
  return modules.map(({ submenu, group, ...item }) => ({
    ...createMenuItem(item, { t, exists }),
    submenu: submenu?.map((submenuItem) => createMenuItem(submenuItem as SidebarItem, { t, exists })),
    onContextMenu: (event) => handleRightClick(event, item.route),
    group: group ?? DEFAULT_GROUP,
  }));
};

export const groupModules = (modules: SidebarMenuItemEntity[]) => {
  const sidebarGroupMap = new Map<string, SidebarMenuItemEntity[]>([[DEFAULT_GROUP, []]]);

  modules.forEach((module) => {
    const { group } = module;
    const groupMap = sidebarGroupMap.get(group);
    if (!groupMap) {
      sidebarGroupMap.set(group, castArray(module));
      return;
    }
    groupMap.push(module);
  });
  return sidebarGroupMap;
};

export const sortSidebarItems = (sidebarItems) => {
  const storedItemCustomization =
    (services.appFrame.userStorage.getItem(SIDEBAR_ITEM_ORDER_NAMESPACE) as SidebarCustomizationMap) ?? {};

  if (!storedItemCustomization) {
    return sidebarItems;
  }

  const configuredItems: SavedSidebarItem[] = [];
  const newItems: SavedSidebarItemBase[] = [];

  sidebarItems.forEach((sidebarItem: SavedSidebarItem) => {
    if (!storedItemCustomization[sidebarItem.id]) {
      newItems.push(sidebarItem);
      return;
    }
    const itemConfig = storedItemCustomization[sidebarItem.id];
    configuredItems.push({ ...sidebarItem, customizationProps: itemConfig });
  });

  const sortedItems = sortBy(configuredItems, (item) => {
    return item.customizationProps.customizedOrder;
  });

  return [...sortedItems, ...newItems];
};

export const isSidebarPinningDisabled = (items: (SidebarMenuItemEntity | ReactNode)[]) => {
  const sidebarPinningMode = getMainConfig('sidebarPinningMode') || 'always';
  if (sidebarPinningMode === 'never') {
    return true;
  }

  if (sidebarPinningMode === 'always') {
    return false;
  }

  // auto mode
  return some(
    items,
    (item) => isValidElement(item) || (isObject(item) ? (item as SidebarMenuItemEntity).submenu : true),
  );
};
